import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Pressable,
  TextInput,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useRouter } from 'expo-router';
import { AntDesign, Feather } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchGroups } from '../../services/groupService';
import { createZoneSheetVisibleAtom } from '../../atoms';
import { Group, ZONE_TOPICS, ZONE_TYPE_INFO, ZoneTopic } from '../../types';
import CachedImage from '../../components/CachedImage';
import PixelContainer from '../../components/PixelContainer';
import { fontStyle, getFontFamily } from '../../config/fonts';

export default function ZonesScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const router = useRouter();
  const [search, setSearch] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<ZoneTopic | null>(null);
  const setCreateZoneSheetVisible = useSetAtom(createZoneSheetVisibleAtom);

  const { data: zones, isLoading, refetch } = useQuery({
    queryKey: ['groups', search, selectedTopic],
    queryFn: () => fetchGroups({ search, topic: selectedTopic || undefined }),
    staleTime: 30000,
  });

  const handleCreateZone = () => {
    setCreateZoneSheetVisible(true);
  };

  const handleZonePress = (zone: Group) => {
    router.push(`/zone/${zone.id}`);
  };

  const renderTopicFilter = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.topicsContainer}
      contentContainerStyle={styles.topicsContent}
    >
      {/* All Topics button */}
      <Pressable onPress={() => setSelectedTopic(null)}>
        <PixelContainer
          backgroundColor={!selectedTopic ? colors.primary : colors.surface}
          cornerSize={3}
          style={styles.topicChip}
        >
          <Text
            style={[
              styles.topicChipText,
              fontStyle,
              { color: colors.text },
              !selectedTopic && { color: '#fff' },
            ]}
          >
            {t('zone.allTopics')}
          </Text>
        </PixelContainer>
      </Pressable>

      {/* Topic chips */}
      {ZONE_TOPICS.map((topic) => (
        <Pressable
          key={topic}
          onPress={() => setSelectedTopic(selectedTopic === topic ? null : topic)}
        >
          <PixelContainer
            backgroundColor={selectedTopic === topic ? colors.primary : colors.surface}
            cornerSize={3}
            style={styles.topicChip}
          >
            <Text
              style={[
                styles.topicChipText,
                fontStyle,
                { color: colors.text },
                selectedTopic === topic && { color: '#fff' },
              ]}
            >
              {t(`zone.topics.${topic}`)}
            </Text>
          </PixelContainer>
        </Pressable>
      ))}
    </ScrollView>
  );

  const renderZoneItem = ({ item }: { item: Group }) => {
    const typeInfo = ZONE_TYPE_INFO[item.type];

    return (
      <Pressable
        style={[styles.zoneCard, { backgroundColor: colors.background, borderColor: colors.border }]}
        onPress={() => handleZonePress(item)}
      >
        {/* Zone Image */}
        <View style={styles.zoneImageContainer}>
          {item.image ? (
            <CachedImage uri={item.image} style={styles.zoneImage} />
          ) : (
            <View style={[styles.zoneImage, styles.zoneImagePlaceholder, { backgroundColor: colors.surface }]}>
              <Feather name="users" size={24} color={colors.textSecondary} />
            </View>
          )}
          {/* Type Badge */}
          <View style={[styles.typeBadge, { backgroundColor: typeInfo.color }]}>
            <Text style={styles.typeBadgeEmoji}>{typeInfo.emoji}</Text>
          </View>
        </View>

        {/* Zone Info Container */}
        <View style={styles.zoneContentContainer}>
          <View style={styles.zoneInfo}>
            <View style={styles.zoneHeader}>
              <Text style={[styles.zoneName, fontStyle, { color: colors.text }]} numberOfLines={1}>
                {item.name}
              </Text>
              <Text style={[styles.zoneType, fontStyle, { color: typeInfo.color }]}>
                {t(`zone.types.${item.type}`)}
              </Text>
            </View>

            {item.description && (
              <Text style={[styles.zoneDescription, fontStyle, { color: colors.textSecondary }]} numberOfLines={2}>
                {item.description}
              </Text>
            )}

            {/* Topics */}
            {item.topics && item.topics.length > 0 && (
              <View style={styles.zoneTopics}>
                {item.topics.slice(0, 3).map((topic) => (
                  <View
                    key={topic}
                    style={[styles.zoneTopicTag, { backgroundColor: colors.surface }]}
                  >
                    <Text style={[styles.zoneTopicText, fontStyle, { color: colors.textSecondary }]}>
                      {t(`zone.topics.${topic}`)}
                    </Text>
                  </View>
                ))}
                {item.topics.length > 3 && (
                  <Text style={[styles.moreTopics, fontStyle, { color: colors.textSecondary }]}>
                    +{item.topics.length - 3}
                  </Text>
                )}
              </View>
            )}

            {/* Server-specific: Online status */}
            {item.type === 'server' && item.serverFields && (
              <View style={styles.serverStatus}>
                <View
                  style={[
                    styles.statusDot,
                    { backgroundColor: item.serverFields.isOnline ? '#10B981' : '#EF4444' },
                  ]}
                />
                <Text style={[styles.statusText, fontStyle, { color: colors.textSecondary }]}>
                  {item.serverFields.isOnline ? t('zone.online') : t('zone.offline')}
                  {item.serverFields.isOnline && item.serverFields.playerCount !== undefined && (
                    ` - ${item.serverFields.playerCount}/${item.serverFields.maxPlayers || '?'} ${t('zone.players')}`
                  )}
                </Text>
              </View>
            )}

            {/* Guild-specific: Recruiting status */}
            {item.type === 'guild' && item.guildFields && (
              <View style={styles.guildStatus}>
                <Text
                  style={[
                    styles.recruitingBadge,
                    fontStyle,
                    {
                      color: item.guildFields.isRecruiting ? '#10B981' : colors.textSecondary,
                    },
                  ]}
                >
                  {item.guildFields.isRecruiting ? t('zone.recruiting') : t('zone.notRecruiting')}
                </Text>
              </View>
            )}

            {/* Members count and Join Button */}
            <View style={styles.membersAndButtonRow}>
              <Text style={[styles.zoneMembers, fontStyle, { color: colors.textSecondary }]}>
                {item.membersCount || 0} {(item.membersCount || 0) === 1 ? t('zone.member') : t('zone.members')}
              </Text>
              <Pressable>
                <PixelContainer backgroundColor={colors.primary} cornerSize={4} style={styles.joinButton}>
                  <Text style={[styles.joinButtonText, fontStyle]}>{t('common.join')}</Text>
                </PixelContainer>
              </Pressable>
            </View>
          </View>
        </View>
      </Pressable>
    );
  };

  const renderEmpty = () => {
    if (isLoading) return null;
    return (
      <View style={styles.emptyContainer}>
        <Feather name="users" size={48} color={colors.textSecondary} />
        <Text style={[styles.emptyText, fontStyle, { color: colors.textSecondary }]}>
          {t('zone.noZones')}
        </Text>
      </View>
    );
  };

  const fontFamily = getFontFamily();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={['bottom']}>
      <View style={styles.headerContainer}>
        {/* Search Bar */}
        <View style={[styles.searchContainer, { backgroundColor: colors.surface }]}>
          <Feather name="search" size={18} color={colors.textSecondary} />
          <TextInput
            style={[styles.searchInput, fontStyle, { color: colors.text }]}
            placeholder={t('zone.search')}
            placeholderTextColor={colors.textSecondary}
            value={search}
            onChangeText={setSearch}
          />
          {search.length > 0 && (
            <Pressable onPress={() => setSearch('')}>
              <AntDesign name="closecircle" size={16} color={colors.textSecondary} />
            </Pressable>
          )}
        </View>

        {/* Topic Filters */}
        {renderTopicFilter()}
      </View>

      {/* Zones List */}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={zones || []}
          renderItem={renderZoneItem}
          keyExtractor={(item) => item.id}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onRefresh={refetch}
          refreshing={isLoading}
          style={styles.list}
        />
      )}

      {/* FAB to create zone */}
      <Pressable
        style={[styles.fab, { backgroundColor: colors.primary }]}
        onPress={handleCreateZone}
      >
        <AntDesign name="plus" size={24} color="#fff" />
      </Pressable>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerContainer: {
    zIndex: 1,
    flexShrink: 0,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
  },
  topicsContainer: {
    marginTop: 12,
    marginBottom: 8,
  },
  topicsContent: {
    paddingHorizontal: 16,
    gap: 8,
  },
  topicChip: {
    paddingHorizontal: 14,
    paddingVertical: 4,
  },
  topicChipText: {
    fontSize: 13,
    fontWeight: '700',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 100,
  },
  zoneCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    marginBottom: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  zoneImageContainer: {
    marginRight: 12,
    position: 'relative',
  },
  zoneImage: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  zoneImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#fff',
  },
  typeBadgeEmoji: {
    fontSize: 10,
  },
  zoneContentContainer: {
    flex: 1,
  },
  zoneInfo: {
    flex: 1,
  },
  zoneHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  zoneName: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  zoneType: {
    fontSize: 11,
    fontWeight: '600',
  },
  zoneDescription: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 6,
  },
  zoneTopics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 6,
  },
  zoneTopicTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  zoneTopicText: {
    fontSize: 11,
  },
  moreTopics: {
    fontSize: 11,
    alignSelf: 'center',
  },
  serverStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 12,
  },
  guildStatus: {
    marginBottom: 4,
  },
  recruitingBadge: {
    fontSize: 12,
    fontWeight: '500',
  },
  membersAndButtonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  zoneMembers: {
    fontSize: 12,
  },
  joinButton: {
    paddingHorizontal: 14,
    paddingVertical: 2,
  },
  joinButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
    gap: 12,
  },
  emptyText: {
    fontSize: 16,
  },
  fab: {
    position: 'absolute',
    right: 20,
    bottom: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
});
